/**
 * Turning an org redaction code into the assignment a document carries.
 *
 * Two paths produce a `RedactionCodeAssignment` and they must agree, because
 * the reviewer sees both on the same page: `RedactionCodeModal` (assigned by
 * hand) and the lexicon apply (assigned from the entry's `redactionCodeId`,
 * VE-24948 / `U5-FR8`). A code stamped one way and the same code stamped the
 * other must be indistinguishable on the box, in burn-in and in the disclosure
 * report — so the id/label/colour derivation lives here once rather than being
 * written twice.
 *
 * WHY THE COLOUR IS VALIDATED. `RedactionCode.codeColor` is free text on an
 * SDO: nothing at the settings end guarantees it parses as a hex colour. The
 * modal has a human present who can see a broken swatch and pick another; the
 * apply path has nobody, and an unusable value would reach burn-in, where the
 * stamp is drawn. Falling back is therefore not defensive padding — it is the
 * only place the invalid value can be caught.
 */

import { RedactionCode } from '@redact-modules/redactionCodes/models';
import { RedactionCodeAssignment } from './models';

/** `#rgb` or `#rrggbb`. The one shape the editor's colour inputs emit. */
export const REDACTION_CODE_HEX_REGEX = /^#([0-9a-f]{3}){1,2}$/i;

/**
 * Used when a code's configured colour cannot be parsed. White, matching the
 * first preset swatch the modal offers, so a fallback looks like a deliberate
 * choice rather than a rendering fault.
 */
export const DEFAULT_REDACTION_CODE_COLOR = '#FFFFFF';

export function isValidRedactionCodeColor(color: string | undefined): boolean {
  return typeof color === 'string' && REDACTION_CODE_HEX_REGEX.test(color);
}

/**
 * The assignment for `code`, with its configured colour when that colour is
 * usable and the default swatch when it is not.
 *
 * `label` is the SHORT code string (`B6`), not `codeName` — that is what gets
 * stamped onto the redaction box and written to the report's code column.
 */
export function redactionCodeAssignmentFor(
  code: RedactionCode
): RedactionCodeAssignment {
  return {
    id: code.id,
    label: code.code,
    color: isValidRedactionCodeColor(code.codeColor)
      ? code.codeColor
      : DEFAULT_REDACTION_CODE_COLOR,
  };
}
